/**
 * Campaign Wizard Types
 *
 * Types for the AI-guided campaign creation wizard with human-in-the-loop confirmation.
 */

export type WizardType = 'recruiting' | 'sales' | 'account';

export type CampaignWizardStep =
  | 'type_selection'
  | 'name_account'
  | 'briefing'
  | 'targeting'
  | 'contacts'
  | 'messages'
  | 'flow'
  | 'settings'
  | 'review'
  | 'validation'
  | 'complete';

export type AccountWizardStep =
  | 'instructions'
  | 'cookie_input'
  | 'two_factor'
  | 'syncing'
  | 'complete';

export type WizardStepStatus = 'pending' | 'active' | 'confirmed' | 'skipped' | 'error';

/**
 * Base wizard step definition
 */
export interface WizardStepDefinition<TStep extends string = string> {
  id: TStep;
  name: string;
  description: string;
  required: boolean;
  confirmationRequired: boolean;
}

/**
 * Campaign wizard step definitions
 */
export const CAMPAIGN_WIZARD_STEPS: WizardStepDefinition<CampaignWizardStep>[] = [
  {
    id: 'type_selection',
    name: 'Campaign Type',
    description: 'Choose between recruiting or sales campaign',
    required: true,
    confirmationRequired: true,
  },
  {
    id: 'name_account',
    name: 'Name & Account',
    description: 'Set campaign name and select LinkedIn account',
    required: true,
    confirmationRequired: true,
  },
  {
    id: 'briefing',
    name: 'Briefing',
    description: 'Describe your job/product and target audience',
    required: true,
    confirmationRequired: true,
  },
  {
    id: 'targeting',
    name: 'Target Audience',
    description: 'Define who you want to reach',
    required: true,
    confirmationRequired: true,
  },
  {
    id: 'contacts',
    name: 'Contact Source',
    description: 'Choose how to add contacts to the campaign',
    required: true,
    confirmationRequired: true,
  },
  {
    id: 'messages',
    name: 'Messages',
    description: 'Create personalized message templates',
    required: true,
    confirmationRequired: true,
  },
  {
    id: 'flow',
    name: 'Campaign Flow',
    description: 'Design your outreach sequence',
    required: true,
    confirmationRequired: true,
  },
  {
    id: 'settings',
    name: 'Settings',
    description: 'Configure working hours and limits',
    required: true,
    confirmationRequired: true,
  },
  {
    id: 'review',
    name: 'Review',
    description: 'Review all campaign settings',
    required: true,
    confirmationRequired: false,
  },
  {
    id: 'validation',
    name: 'Quality Check',
    description: 'AI validates your campaign before launch',
    required: true,
    confirmationRequired: true,
  },
  {
    id: 'complete',
    name: 'Complete',
    description: 'Campaign is ready to launch',
    required: false,
    confirmationRequired: false,
  },
];

/**
 * Campaign wizard data collected at each step
 */
export interface CampaignWizardData {
  // Step 1: Type Selection
  campaignType?: 'recruiting' | 'sales';

  // Step 2: Name & Account
  campaignName?: string;
  linkedinAccountId?: string;

  // Step 3: Briefing (Recruiting)
  jobTitle?: string;
  jobDescription?: string;
  companyBenefits?: string;

  // Step 3: Briefing (Sales)
  productName?: string;
  productDescription?: string;
  problemsSolved?: string;
  keyBenefits?: string;

  // Step 4: Targeting
  targetJobTitles?: string[];
  targetIndustries?: string[];
  targetLocations?: string[];
  targetCompanySize?: string[];
  additionalCriteria?: string;

  // Step 5: Contacts
  contactSource?: 'linkedin_search' | 'csv_import' | 'manual' | 'existing';
  searchUrl?: string;
  csvFile?: File;
  contactIds?: string[];
  estimatedContacts?: number;

  // Step 6: Messages
  connectionRequest?: string;
  followUpMessages?: Array<{
    delay: number;
    delayUnit: 'hours' | 'days';
    message: string;
  }>;
  inMailMessage?: string;

  // Step 7: Flow
  flowSteps?: Array<{
    type: string;
    delay?: number;
    condition?: string;
  }>;

  // Step 8: Settings
  workingDays?: string[];
  workingHoursStart?: string;
  workingHoursEnd?: string;
  dailyConnectionLimit?: number;
  dailyMessageLimit?: number;
  blacklistIds?: string[];

  // Metadata
  campaignId?: string;
  createdAt?: Date;
  lastModifiedAt?: Date;
}

/**
 * Wizard state for a single step
 */
export interface WizardStepState<TStep extends string = string> {
  step: TStep;
  status: WizardStepStatus;
  data: Record<string, unknown>;
  error?: string;
  confirmedAt?: Date;
}

/**
 * Complete wizard state
 */
export interface CampaignWizardState {
  id: string;
  type: 'recruiting' | 'sales';
  currentStep: CampaignWizardStep;
  steps: Record<CampaignWizardStep, WizardStepState<CampaignWizardStep>>;
  data: CampaignWizardData;
  validation?: ValidationResult;
  createdAt: Date;
  lastModifiedAt: Date;
  campaignId?: string;
}

/**
 * Validation severity levels
 */
export type ValidationSeverity = 'error' | 'warning' | 'info';

/**
 * Validation issue categories
 */
export type ValidationCategory =
  | 'message_quality'
  | 'targeting_quality'
  | 'sequence_logic'
  | 'account_readiness'
  | 'compliance'
  | 'best_practices';

/**
 * Single validation issue
 */
export interface ValidationIssue {
  id: string;
  severity: ValidationSeverity;
  category: ValidationCategory;
  title: string;
  message: string;
  suggestion?: string;
  field?: string;
  autoFixable?: boolean;
}

/**
 * Complete validation result
 */
export interface ValidationResult {
  passed: boolean;
  score: number; // 0-100
  issues: ValidationIssue[];
  checkedAt: Date;
  summary: string;
}

/**
 * Step confirmation payload
 */
export interface StepConfirmation {
  stepId: CampaignWizardStep;
  confirmed: boolean;
  modifications?: Record<string, unknown>;
}

/**
 * Wizard actions available in chat
 */
export type WizardAction =
  | { type: 'START_WIZARD'; wizardType: WizardType }
  | { type: 'UPDATE_STEP_DATA'; step: CampaignWizardStep; data: Record<string, unknown> }
  | { type: 'CONFIRM_STEP'; step: CampaignWizardStep }
  | { type: 'EDIT_STEP'; step: CampaignWizardStep }
  | { type: 'SKIP_STEP'; step: CampaignWizardStep }
  | { type: 'GO_TO_STEP'; step: CampaignWizardStep }
  | { type: 'RUN_VALIDATION' }
  | { type: 'FIX_ISSUE'; issueId: string }
  | { type: 'LAUNCH_CAMPAIGN' }
  | { type: 'SAVE_DRAFT' }
  | { type: 'CANCEL_WIZARD' };

